import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  LikeOutlined,
  DislikeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  feedbackApiService,
  FeedbackPayload,
} from 'modules/chatbot/services/feedbackApiService';
import {
  selectAllMessages,
  selectActiveSessionId,
  selectContext,
} from 'modules/chatbot/slices/chatbotSlice';
import { Sender } from 'modules/chatbot/enums/Sender';
import styles from './messageFeedback.module.less';

// Enums
export enum FeedbackType {
  THUMBS_UP = 'positive',
  THUMBS_DOWN = 'negative',
}

export enum FeedbackState {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

// Constants
const FEEDBACK_MESSAGES = {
  [FeedbackState.SUCCESS]: 'Rate this response:',
  [FeedbackState.ERROR]: 'Failed',
  [FeedbackState.IDLE]: 'Rate this response:',
  [FeedbackState.LOADING]: 'Rate this response:',
} as const;

const ANIMATION_TIMEOUTS = {
  SUCCESS_DURATION: 2000,
  ERROR_DURATION: 3000,
} as const;

const ARIA_LABELS = {
  [FeedbackType.THUMBS_UP]: 'Thumbs up - Good response',
  [FeedbackType.THUMBS_DOWN]: 'Thumbs down - Poor response',
} as const;

// Icon mapping for different feedback types and states
const createIconComponents = (styles: any) =>
  ({
    [FeedbackType.THUMBS_UP]: {
      default: <LikeOutlined className={styles.defaultIcon} />,
      loading: <LoadingOutlined className={styles.loadingIcon} />,
      selected: <LikeOutlined className={styles.successIcon} />,
    },
    [FeedbackType.THUMBS_DOWN]: {
      default: <DislikeOutlined className={styles.defaultIcon} />,
      loading: <LoadingOutlined className={styles.loadingIcon} />,
      selected: <DislikeOutlined className={styles.successIcon} />,
    },
  } as const);

type FeedbackTypeValue = FeedbackType | null;

interface MessageFeedbackProps {
  messageId?: string;
  isPending?: boolean;
  onFeedback?: (messageId: string, feedback: FeedbackTypeValue) => void;
  userId?: string;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  messageId,
  isPending = false,
  onFeedback,
  userId,
}: MessageFeedbackProps) => {
  const [feedback, setFeedback] = useState<FeedbackTypeValue>(null);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(
    FeedbackState.IDLE
  );
  const [loadingType, setLoadingType] = useState<FeedbackTypeValue>(null);

  // Redux selectors to get context data
  const messages = useSelector(selectAllMessages);
  const activeSessionId = useSelector(selectActiveSessionId);
  const context = useSelector(selectContext);
  // Custom selector to get current session data from sessions array
  const currentSession = useSelector((state: any) => {
    const { chatBot } = state;
    if (
      !chatBot.activeSessionId ||
      !chatBot.sessions[chatBot.activeSessionId]
    ) {
      return null;
    }
    return chatBot.sessions[chatBot.activeSessionId];
  });

  const submitFeedback = useCallback(
    async (type: FeedbackType) => {
      if (!messageId) return false;

      // Find the current message and get context
      const currentMessage = messages.find((msg) => msg.id === messageId);
      const currentMessageIndex = messages.findIndex(
        (msg) => msg.id === messageId
      );

      // Get the user question (previous message if current is bot message)
      let userQuestion = '';
      if (currentMessage?.sender === Sender.BOT && currentMessageIndex > 0) {
        const previousMessage = messages[currentMessageIndex - 1];
        if (previousMessage?.sender === Sender.USER) {
          userQuestion = previousMessage.content;
        }
      }

      // Get AI answer (current message content if it's from bot)
      const aiAnswer =
        currentMessage?.sender === Sender.BOT ? currentMessage.content : '';

      const payload: FeedbackPayload = {
        messageId,
        feedback: type,
        timestamp: new Date().toISOString(),
        userId,
        sessionId: activeSessionId || '',
        userQuestion,
        aiAnswer,
        benefitGuideId: context.benefitGuideId,
        employerId: context.benefitGuideContent?.employerId,
        conversationContext: {
          currentSession: currentSession,
        },
      };

      const result = await feedbackApiService.submitFeedback(payload);
      return result.success;
    },
    [messageId, userId, activeSessionId, messages, context, currentSession]
  );

  const handleFeedback = useCallback(
    async (type: FeedbackType) => {
      // If already selected, deselect it
      if (feedback === type) {
        setFeedback(null);
        setFeedbackState(FeedbackState.IDLE);
        onFeedback?.(messageId || '', null);
        return;
      }

      // Set loading state
      setLoadingType(type);
      setFeedbackState(FeedbackState.LOADING);

      try {
        // Submit to API with comprehensive context
        const success = await submitFeedback(type);

        if (success) {
          setFeedback(type);
          setFeedbackState(FeedbackState.SUCCESS);
          onFeedback?.(messageId || '', type);

          // Show success animation, then return to normal
          setTimeout(() => {
            setFeedbackState(FeedbackState.IDLE);
          }, ANIMATION_TIMEOUTS.SUCCESS_DURATION);
        } else {
          setFeedbackState(FeedbackState.ERROR);

          // Show error state, then return to normal
          setTimeout(() => {
            setFeedbackState(FeedbackState.IDLE);
          }, ANIMATION_TIMEOUTS.ERROR_DURATION);
        }
      } catch (error) {
        console.error('Error submitting feedback:', error);
        setFeedbackState(FeedbackState.ERROR);

        setTimeout(() => {
          setFeedbackState(FeedbackState.IDLE);
        }, ANIMATION_TIMEOUTS.ERROR_DURATION);
      }

      setLoadingType(null);
    },
    [feedback, messageId, onFeedback, submitFeedback]
  );

  // Don't render if message is pending or if we don't have a valid messageId
  if (isPending || !messageId) {
    return null;
  }

  const renderButtonContent = (type: FeedbackType) => {
    const iconComponents = createIconComponents(styles);
    const isLoading =
      loadingType === type && feedbackState === FeedbackState.LOADING;
    const isSelected = feedback === type;

    if (isLoading) {
      return iconComponents[type].loading;
    }

    if (isSelected) {
      return iconComponents[type].selected;
    }

    return iconComponents[type].default;
  };

  // CSS class mapping for different button states
  const getButtonStateClasses = (type: FeedbackType) => ({
    [styles.selected]: feedback === type,
    [styles.loading]:
      loadingType === type && feedbackState === FeedbackState.LOADING,
    [styles.error]: feedbackState === FeedbackState.ERROR,
    [styles.disabled]:
      feedbackState === FeedbackState.LOADING && loadingType !== type,
  });

  const getButtonClassName = (type: FeedbackType) => {
    return classNames(styles.feedbackButton, getButtonStateClasses(type));
  };

  // Container class mapping for different feedback states
  const getContainerStateClasses = () => ({
    [styles.errorContainer]: feedbackState === FeedbackState.ERROR,
  });

  return (
    <div
      className={classNames(
        styles.feedbackContainer,
        getContainerStateClasses()
      )}
    >
      <div className={styles.feedbackGrid}>
        <span className={styles.feedbackLabel}>
          {FEEDBACK_MESSAGES[feedbackState]}
        </span>
        <button
          type="button"
          className={getButtonClassName(FeedbackType.THUMBS_UP)}
          onClick={() => handleFeedback(FeedbackType.THUMBS_UP)}
          disabled={feedbackState === FeedbackState.LOADING}
          aria-label={ARIA_LABELS[FeedbackType.THUMBS_UP]}
        >
          {renderButtonContent(FeedbackType.THUMBS_UP)}
        </button>
        <button
          type="button"
          className={getButtonClassName(FeedbackType.THUMBS_DOWN)}
          onClick={() => handleFeedback(FeedbackType.THUMBS_DOWN)}
          disabled={feedbackState === FeedbackState.LOADING}
          aria-label={ARIA_LABELS[FeedbackType.THUMBS_DOWN]}
        >
          {renderButtonContent(FeedbackType.THUMBS_DOWN)}
        </button>
      </div>
    </div>
  );
};

export default MessageFeedback;
